import { FormRichTextEditor } from '@exam-management/rich-text-editor';
import { AddRoundedIcon, Button, ErrorOutlineIcon, Flex, Text } from '@shared/components';
import { useFormContext } from 'react-hook-form';

import { useUploadImageForSingleQuestionCreation } from '../../hooks';
import type { SingleQuestionCreationFormInput } from '../single-question-creation-form';

import { AnswerFormFields } from './answer-form-fields';

interface DetailsFormSectionProps {
  questionId?: string;
  taskId: string;
}

export default function DetailsFormSection(props: Readonly<DetailsFormSectionProps>) {
  const { questionId, taskId } = props;
  const formContext = useFormContext<Pick<SingleQuestionCreationFormInput, 'detail'>>();

  const { handleUpload, loading: isLoading } = useUploadImageForSingleQuestionCreation({
    questionId,
    taskId,
  });

  const choices = formContext.watch('detail.choices') ?? [];
  const correctAnswer = formContext.watch('detail.correctAnswer');

  const handleAddAnswer = () => {
    const newChoiceValue = choices.length + 1;
    formContext.setValue('detail.choices', [
      ...choices,
      {
        value: newChoiceValue.toString(),
        order: newChoiceValue,
        label: '',
      },
    ]);
  };

  const handleDeleteAnswer = (index: number) => {
    if (correctAnswer.value === choices[index].value) {
      formContext.setValue('detail.correctAnswer', { value: '' });
    }
    const choicesWithoutDeletedIndex = choices.filter((item) => item.order !== index + 1);
    for (const choice of choicesWithoutDeletedIndex) {
      if (choice.order > index + 1) {
        const newChoiceValue = (parseInt(choice.value, 10) - 1).toString();
        if (correctAnswer.value === choice.value) {
          formContext.setValue('detail.correctAnswer', { value: newChoiceValue });
        }
        choice.order = choice.order - 1;
        choice.value = newChoiceValue;
      }
    }
    formContext.setValue('detail.choices', choicesWithoutDeletedIndex);
    formContext.clearErrors(`detail.choices.${index}.label`);
  };

  const choicesAndCorrectAnswerErrorMessages =
    formContext.formState.errors.detail?.choices?.root?.message ||
    formContext.formState.errors.detail?.correctAnswer?.value?.message ||
    formContext.formState.errors.detail?.choices?.message;
  const hasError = !!choicesAndCorrectAnswerErrorMessages;

  return (
    <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(4) })}>
      <FormRichTextEditor
        name='detail.details'
        onImageUpload={handleUpload}
        loading={isLoading}
        placeholder='กรอกคำถามที่นี่'
        rows={2}
      />
      <Flex
        sx={(theme) => ({
          gap: theme.spacing(4),
          width: '100%',
          flexDirection: 'column',
        })}
      >
        <Text variant='body2' color='text.secondary'>
          กรุณาเลือกคำตอบที่ถูกต้อง 1 ข้อ
        </Text>
        {hasError ? (
          <Flex
            sx={(theme) => ({ color: 'error.main', gap: theme.spacing(1), alignItems: 'center' })}
          >
            <ErrorOutlineIcon />
            <Text variant='body2'>{choicesAndCorrectAnswerErrorMessages}</Text>
          </Flex>
        ) : null}
        {choices.map((choice, index) => {
          const isCorrectAnswer = correctAnswer.value === choice.value;

          return (
            <AnswerFormFields
              key={`qcontent-answer-${choice.value}`}
              correctAnswerName='detail.correctAnswer.value'
              choiceName={`detail.choices.${index}.label`}
              index={index}
              label={`ตัวเลือกที่ ${choice.order}`}
              value={choice.value}
              onDelete={() => handleDeleteAnswer(index)}
              questionId={questionId}
              taskId={taskId}
              correct={isCorrectAnswer}
            />
          );
        })}
      </Flex>
      <Button
        color='secondary'
        variant='outlined'
        onClick={handleAddAnswer}
        startIcon={<AddRoundedIcon fontSize='small' />}
        sx={{
          width: 'fit-content',
        }}
      >
        เพิ่มตัวเลือกใหม่
      </Button>
    </Flex>
  );
}
